import { testoErrore, useT } from "../lib/i18n.tsx";
import { useEffect, useRef, useState } from "react";
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from "react";

/**
 * Elementi di base dell'interfaccia.
 *
 * Scritti a mano invece di importare una libreria di componenti: servono poche
 * primitive e la coerenza si ottiene meglio con trenta righe di Tailwind che con
 * venti dipendenze da tenere aggiornate.
 */

export const cx = (...classes: (string | false | null | undefined)[]) =>
  classes.filter(Boolean).join(" ");

type Variant = "primary" | "secondary" | "ghost" | "danger";

const VARIANTS: Record<Variant, string> = {
  primary: "bg-accent-600 text-white hover:bg-accent-700 disabled:hover:bg-accent-600",
  secondary: "superficie hover:bg-[var(--superficie-tenue)]",
  ghost: "hover:bg-[var(--superficie-tenue)]",
  danger: "bg-red-600 text-white hover:bg-red-700 disabled:hover:bg-red-600",
};

export function Button({
  variant = "secondary",
  className,
  loading,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; loading?: boolean }) {
  return (
    <button
      {...props}
      disabled={props.disabled || loading}
      className={cx(
        "inline-flex items-center justify-center gap-2 rounded-lg px-3.5 py-2 text-sm font-medium",
        "transition-colors disabled:cursor-not-allowed disabled:opacity-50",
        VARIANTS[variant],
        className,
      )}
    >
      {loading && <Spinner />}
      {children}
    </button>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <svg className={cx("size-4 animate-spin", className)} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
      <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={cx(
        "w-full rounded-lg border bg-[var(--superficie)] px-3 py-2 text-sm",
        "placeholder:text-[var(--testo-tenue)] disabled:opacity-60",
        className,
      )}
    />
  );
}

export function Textarea({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      spellCheck={false}
      className={cx(
        "w-full rounded-lg border bg-[var(--superficie)] px-3 py-2 font-mono text-[13px] leading-relaxed",
        "resize-y placeholder:text-[var(--testo-tenue)]",
        className,
      )}
    />
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <label className="block space-y-1.5">
      <span className="text-sm font-medium">{label}</span>
      {children}
      {hint && <span className="block text-xs tenue">{hint}</span>}
    </label>
  );
}

type Tone = "neutral" | "success" | "warning" | "danger" | "accent";

const TONES: Record<Tone, string> = {
  neutral: "bg-[var(--superficie-tenue)] tenue",
  success: "bg-emerald-500/12 text-emerald-700 dark:text-emerald-400",
  warning: "bg-amber-500/12 text-amber-700 dark:text-amber-400",
  danger: "bg-red-500/12 text-red-700 dark:text-red-400",
  accent: "bg-accent-500/12 text-accent-700 dark:text-accent-500",
};

export function Badge({ tone = "neutral", children }: { tone?: Tone; children: ReactNode }) {
  return (
    <span
      className={cx(
        "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium",
        TONES[tone],
      )}
    >
      {children}
    </span>
  );
}

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx("superficie rounded-xl", className)}>{children}</div>;
}

/**
 * Menu a comparsa su <details>: Invio e Spazio sul riepilogo lo aprono già
 * da tastiera. Qui si aggiungono Esc (con il fuoco che torna al riepilogo) e
 * il clic fuori; dopo la scelta di una voce lo chiude `VoceMenu`.
 */
export function Menu({
  etichetta,
  children,
  allineamento = "destra",
}: {
  etichetta: ReactNode;
  children: ReactNode;
  allineamento?: "sinistra" | "destra";
}) {
  const ref = useRef<HTMLDetailsElement>(null);

  useEffect(() => {
    const fuori = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) ref.current.open = false;
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || !ref.current?.open) return;
      ref.current.open = false;
      ref.current.querySelector("summary")?.focus();
    };
    document.addEventListener("pointerdown", fuori);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("pointerdown", fuori);
      document.removeEventListener("keydown", esc);
    };
  }, []);

  return (
    <details ref={ref} className="relative">
      <summary className="flex cursor-pointer list-none items-center gap-1 rounded-lg px-2 py-1.5 hover:bg-[var(--superficie-tenue)] [&::-webkit-details-marker]:hidden">
        {etichetta}
      </summary>
      <div
        className={cx(
          "superficie absolute top-full z-20 mt-1 w-64 max-w-[calc(100vw-2rem)] rounded-xl p-1 shadow-lg",
          allineamento === "destra" ? "right-0" : "left-0",
        )}
      >
        {children}
      </div>
    </details>
  );
}

/** Voce di un `Menu`: un pulsante a tutta larghezza che, scelto, chiude il menu. */
export function VoceMenu({
  pericolosa,
  className,
  onClick,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { pericolosa?: boolean }) {
  return (
    <button
      type="button"
      {...props}
      onClick={(e) => {
        onClick?.(e);
        e.currentTarget.closest("details")?.removeAttribute("open");
      }}
      className={cx(
        "block w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-[var(--superficie-tenue)] disabled:opacity-50",
        pericolosa && "text-red-600 dark:text-red-400",
        className,
      )}
    />
  );
}

/** Sezione che si apre e si chiude, su <details>. */
export function Apribile({
  titolo,
  children,
  apertaIniziale,
}: {
  titolo: ReactNode;
  /** Una funzione viene chiamata solo a sezione aperta: serve a caricare i dati all'apertura. */
  children: ReactNode | (() => ReactNode);
  apertaIniziale?: boolean;
}) {
  const [aperta, setAperta] = useState(apertaIniziale ?? false);

  return (
    <details
      open={apertaIniziale}
      onToggle={(evento) => setAperta(evento.currentTarget.open)}
      className="superficie rounded-xl"
    >
      <summary className="cursor-pointer rounded-xl px-4 py-3 font-medium hover:bg-[var(--superficie-tenue)]">
        {titolo}
      </summary>
      <div className="border-t p-4">{typeof children === "function" ? (aperta ? children() : null) : children}</div>
    </details>
  );
}

/** Messaggio di errore: sempre leggibile, mai un semplice "operazione fallita". */
export function ErrorNotice({ error }: { error: unknown }) {
  const t = useT();
  if (!error) return null;

  const message = testoErrore(error, t);

  return (
    <div className="rounded-lg border border-red-500/30 bg-red-500/8 px-3.5 py-2.5 text-sm text-red-700 dark:text-red-400">
      {message}
    </div>
  );
}

export function Empty({ title, hint }: { title: string; hint?: ReactNode }) {
  return (
    <div className="rounded-xl border border-dashed px-6 py-12 text-center">
      <p className="font-medium">{title}</p>
      {hint && <p className="mt-1 text-sm tenue">{hint}</p>}
    </div>
  );
}
